import {
  WebsiteImage,
  WebsiteTestimonial,
  WebsiteTestimonialBase,
} from '@flipdish/api-client-typescript';
import { ExpansionPanelDetails, FormControl, FormHelperText, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddOutlined';
import { Field, FieldProps, FormikProps, withFormik } from 'formik';
import { isEqual, keyBy } from 'lodash';
import React, { memo, useCallback } from 'react';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { formikValidate } from '../../../helpers/validation';
import { createLoadingSelector } from '../../../selectors/loading.selector';
import { Button } from '../../../ui/Button';
import Spacer from '../../../ui/Spacer';
import {
  ADD_WEBSITE_SETTINGS_TESTIMONIAL,
  addWebsiteSettingsTestimonial,
  DELETE_WEBSITE_SETTINGS_IMAGE,
  DELETE_WEBSITE_SETTINGS_TESTIMONIAL,
  deleteWebsiteSettingsImage,
  deleteWebsiteSettingsTestimonial,
  UPDATE_WEBSITE_SETTINGS_TESTIMONIAL,
  updateWebsiteSettingsTestimonial,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
  uploadWebsiteSettingsImage,
} from '../actions';
import EditWebsiteSettingsTestimonialsItem, {
  Testimonial,
} from './EditWebsiteSettingsTestimonialsItem';
import EditWebsiteSettingsUploadImage from './EditWebsiteSettingsUploadImage';
import { handleImage } from './utils';

const useStyles = makeStyles({
  content: {
    alignItems: 'center',
    flexDirection: 'column',
  },
  section: {
    width: '100%',
  },
  buttons: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
});

const validateImage = (value: FileList | null) =>
  value && value instanceof File && formikValidate.extension(value, ['jpeg', 'png', 'jpg']);
const maxSectionsAmount = 4;

const savingSelector = createLoadingSelector([
  UPDATE_WEBSITE_SETTINGS_TESTIMONIAL,
  ADD_WEBSITE_SETTINGS_TESTIMONIAL,
  DELETE_WEBSITE_SETTINGS_TESTIMONIAL,
  DELETE_WEBSITE_SETTINGS_IMAGE,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
]);

const mapPropsToValues = (props: Props) => {
  const { data: testimonials, image } = props;

  return {
    image,
    testimonials,
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    saving: savingSelector(state),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  createTestimonial: (testimonial: WebsiteTestimonialBase) =>
    dispatch(addWebsiteSettingsTestimonial(testimonial)),
  updateTestimonial: (testimonial: WebsiteTestimonial) =>
    dispatch(updateWebsiteSettingsTestimonial(testimonial)),
  deleteTestimonial: (testimonialId: number) =>
    dispatch(deleteWebsiteSettingsTestimonial(testimonialId)),
  uploadImage: (imageLocation: WebsiteImage.ImageLocationEnum, file) =>
    dispatch(uploadWebsiteSettingsImage(imageLocation, file)),
  deleteImage: (id: number) => dispatch(deleteWebsiteSettingsImage(id)),
});

type FormValues = {
  image?: WebsiteImage;
  testimonials: Testimonial[];
};

type OuterProps = {
  data: WebsiteTestimonial[];
  image?: WebsiteImage;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  FormikProps<FormValues>;

type Props = OuterProps & InnerProps;

export default compose<InnerProps, OuterProps>(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withFormik<Props, FormValues>({
    displayName: 'EditWebsiteSettingsTestimonialsPanelForm',
    enableReinitialize: true,
    mapPropsToValues,
    handleSubmit(values, formikBag) {
      const { testimonials, image } = values;
      const {
        props: {
          deleteTestimonial,
          createTestimonial,
          updateTestimonial,
          data,
          image: previousImage,
          uploadImage,
          deleteImage,
        },
      } = formikBag;
      if (data !== testimonials) {
        const originalMap = keyBy(data, (item) => item.TestimonialId);
        const newMap = keyBy(testimonials, (item) => item.TestimonialId || item.fakeId);

        for (let i = 0; i < data.length; i++) {
          const original = data[i];
          const id = original.TestimonialId;

          if (id) {
            if (id in newMap) {
              if (!isEqual(original, newMap[id])) {
                updateTestimonial(newMap[id] as WebsiteTestimonial);
              }
            } else {
              deleteTestimonial(id);
            }
          }
        }

        for (let i = 0; i < testimonials.length; i++) {
          const item = testimonials[i];
          const id = item.TestimonialId || item.fakeId;

          if (id) {
            if (!(id in originalMap)) {
              createTestimonial(item);
            }
          }
        }
      }

      handleImage(
        uploadImage,
        deleteImage,
        WebsiteImage.ImageLocationEnum.IndexTestimonialsHeader,
        image,
        previousImage
      );
    },
  })
)(
  memo(function EditWebsiteSettingsTestimonialsPanel(props: Props) {
    const { data, saving, values, setFieldValue, submitForm } = props;
    const { testimonials } = values;
    const classes = useStyles();

    const handleSave = useCallback(submitForm, [testimonials, data]);
    const handleChange = useCallback(
      (data: Testimonial) => {
        setFieldValue(
          'testimonials',
          testimonials.map((section) => {
            if (
              (section.fakeId && data.fakeId && section.fakeId === data.fakeId) ||
              (section.TestimonialId &&
                data.TestimonialId &&
                section.TestimonialId === data.TestimonialId)
            ) {
              return data;
            }
            return section;
          })
        );
      },
      [testimonials]
    );
    const handleRemove = useCallback(
      (id: number) => {
        setFieldValue(
          'testimonials',
          testimonials.filter((section) => section.TestimonialId !== id && section.fakeId !== id)
        );
      },
      [testimonials]
    );

    return (
      <ExpansionPanelDetails className={classes.content}>
        <Field validate={validateImage} name="image">
          {(fProps: FieldProps) => {
            const { field, form } = fProps;
            const { value } = field;

            return (
              <>
                <EditWebsiteSettingsUploadImage
                  image={value && value instanceof WebsiteImage ? value.ImageUrl : undefined}
                  fdKey="testimonials_image"
                  onChange={(data: FileList | null) => {
                    form.setFieldValue(field.name, data ? data[0] : undefined);
                  }}
                />
                {form.errors[field.name] ? (
                  <FormControl error fullWidth>
                    <FormHelperText>{form.errors[field.name]}</FormHelperText>
                  </FormControl>
                ) : null}
              </>
            );
          }}
        </Field>
        <Spacer size={16} />
        {testimonials.map((section, index) => (
          <div key={section.TestimonialId || section.fakeId || index} className={classes.section}>
            <EditWebsiteSettingsTestimonialsItem
              index={index}
              data={section}
              disabled={saving}
              onChange={handleChange}
              onRemove={handleRemove}
            />
          </div>
        ))}
        <div className={classes.buttons}>
          <Button
            fdKey="add_testimonial"
            color="primary"
            disabled={saving || testimonials.length >= maxSectionsAmount}
            onClick={() =>
              setFieldValue('testimonials', [...testimonials, { fakeId: Date.now(), Message: '' }])
            }
          >
            <AddIcon />
            <Translate id="Add_testimonial" />
          </Button>
          <Button
            color="primary"
            variant="outlined"
            fdKey="save_testimonials"
            onClick={handleSave}
            disabled={saving}
          >
            <Translate id="Save" />
          </Button>
        </div>
      </ExpansionPanelDetails>
    );
  })
);
